import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { getWiSATablets, updateCustomer } from "../../api/config";
import { http } from "../../api/utils/http";
import { useFetch } from "../../hooks/useFetch";
import { UpdateItemResponse, WisaTablet } from "../../interfaces/be.interfaces";
import styles from "./styles.module.scss";
import Modal from "../../components/Modal/Modal";
import AddCertTablet from "../../components/AddOrEditCertTablet/Add";
import { Icons } from "../../../assets/images";
import EditCertTablet from "../../components/AddOrEditCertTablet/Edit";
import moment from "moment";
import AccessGuard from "../../components/Guards/AccessGuard";
import { PERMISSIONS } from "../../utils/constants";

const CertTablets = () => {
  const navigate = useNavigate();
  const [isAddOpen, setIsAddOpen] = useState(false);
  const [isEditModalOpen, setEditModalOpen] = useState<number | null>(null);

  // Hook to get the list of WiSA tablets
  const {
    data: certWiSATablets,
    error: certWiSATabletsError,
    run,
  } = useFetch<UpdateItemResponse<WisaTablet[]>>({
    url: getWiSATablets,
    runOnMount: true,
  });

  const [isAssignModalOpen, setIsAssignModalOpen] = useState<number | null>(null);

  // Handler for returning tablet back to WiSA
  const handleUpdateCustomer = (idToUpdate = isAssignModalOpen) => {
    http.makePostRequest(
      updateCustomer,
      () => {
        run();
        setIsAssignModalOpen(null);
        toast.success("Tablet information updated!");
      },
      () => {
        console.log("Something went wrong");
      },
      {
        id: idToUpdate,
        vendorId: null,
      }
    );
  };

  return (
    <>
      {certWiSATabletsError ? <div className="errorAlert">{certWiSATabletsError}</div> : null}
      <div className={styles.pageContainer}>
        <div className={`${styles.pageHeader} d-flex justify-content-between`}>
          <p>Manage WiSA Tablets</p>
          <div className={`${styles.headerActions} d-flex justify-content-between align-item-center`}>
            <AccessGuard permission={PERMISSIONS.ADD_TABLET}>
              <button
                onClick={() => {
                  setIsAddOpen(true);
                }}>
                Add new tablet
              </button>
            </AccessGuard>
          </div>
        </div>
        <div className={styles.tableResponsive}>
          {certWiSATablets?.data?.length ? (
            <table className={styles.table}>
              <thead>
                <tr>
                  <th className={styles.center}>IT Tag</th>
                  <th className={styles.center}>Device ID</th>
                  <th className={styles.center}>Manufacturer</th>
                  <th className={styles.center}>Tablet Name</th>
                  <th className={styles.center}>Checked Out To</th>
                  <th className={styles.center}>Last used by</th>
                  <th className={styles.center}>RSSI</th>
                  <th className={styles.center}>Calibration Date</th>
                  <th className={styles.center}>Return Date</th>
                  <th className={styles.center}>Shipped Date</th>
                  <th className={styles.center}>Ship To Tracking ID</th>
                  <th className={styles.center}>Ship From Tracking ID</th>
                  <th className={styles.center}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {certWiSATablets?.data.map((tablet: WisaTablet) => (
                  <tr key={tablet.tablet_device_id}>
                    <td className={styles.center}>{tablet.it_tag}</td>
                    <td className={styles.center}>{tablet.tablet_device_id ?? "-"}</td>
                    <td className={styles.center}>{tablet.tablet_mfg ?? "-"}</td>
                    <td className={styles.center}>{tablet.tablet_name ?? "-"}</td>
                    <td className={styles.center}>{tablet.customer?.customer_name ?? "-"}</td>
                    <td className={styles.center}>{tablet.lastUsedBy?.user?.name ?? "-"}</td>
                    <td className={styles.center}>{tablet.rssi ?? "-"}</td>
                    <td className={styles.center}>
                      {tablet.calibration_date ? moment(tablet.calibration_date).format("YYYY-MM-DD") : "-"}
                    </td>
                    <td className={styles.center}>{tablet.return_date ? moment(tablet.return_date).format("YYYY-MM-DD") : "-"}</td>
                    <td className={styles.center}>{tablet.date_shipped ? moment(tablet.date_shipped).format("YYYY-MM-DD") : "-"}</td>
                    <td className={styles.center}>{tablet.ship_to_tracking_id ?? "-"}</td>
                    <td className={styles.center}>{tablet.ship_from_tracking_id ?? "-"}</td>
                    <td>
                      <div className={styles.actionButtonContainer}>
                        <AccessGuard permission={PERMISSIONS.UPDATE_TABLET}>
                          <Icons.EditIcon
                            className={styles.iconButton}
                            onClick={() => {
                              setEditModalOpen(tablet.id);
                            }}
                          />
                        </AccessGuard>
                        <AccessGuard permission={PERMISSIONS.TABLET_HISTORY}>
                          <Icons.History
                            className={styles.iconButton}
                            onClick={() => navigate(`/manage-wisa-tablets/history/${tablet.id}`)}
                          />
                        </AccessGuard>
                        <AccessGuard permission={PERMISSIONS.UPDATE_TABLET}>
                          {tablet.customer_checked_out_to && (
                            <button
                              onClick={() => {
                                if (!tablet.customer_checked_out_to) {
                                  setIsAssignModalOpen(tablet.id);
                                } else {
                                  handleUpdateCustomer(tablet.id);
                                }
                              }}
                              className={styles.actionButton}>
                              Return to WiSA
                            </button>
                          )}{" "}
                        </AccessGuard>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className={styles.noTablets}>No Tablets Found, Click on Add new tablet to start adding.</div>
          )}
        </div>
      </div>

      <Modal isModalOpen={isAddOpen}>
        <AddCertTablet
          onCancelClick={() => {
            setIsAddOpen(false);
          }}
          onSuccess={() => {
            run();
            setIsAddOpen(false);
          }}
        />
      </Modal>
      <Modal isModalOpen={isEditModalOpen !== null}>
        <EditCertTablet
          onCancelClick={() => {
            setEditModalOpen(null);
          }}
          onSuccess={() => {
            run();
            setEditModalOpen(null);
          }}
          id={isEditModalOpen}
        />
      </Modal>
    </>
  );
};

export default CertTablets;
